import { omit } from 'lodash-es';
import {
  IErrorApiResponse,
  ISuccessApiResponse,
  IValidationError,
} from '@wyz/types';
import {
  CrudOptions,
  OverridableQuery,
  UseQueryOptions,
  restrictApi,
  serialize,
  useCrud,
  useQuery,
  useQueryClient,
} from '@wyz/utils';
import { useApi, useConfig } from '~/common/context';
import { CommonErrorCodesEnumType } from '~/common/types';
import { ConnectQueryKeys } from '~/connect/api';
import {
  ICriteria,
  ICriteriaPayload,
  ICriteriaList,
  ICriteriaUsageError,
  ICriteriaValueUsageError,
  ICriteriaVisibilityFilter,
} from '~/connect/types';

type Options = Pick<
  CrudOptions<ICriteria, ICriteriaPayload>,
  'create' | 'getOneById' | 'update' | 'remove'
> & {
  getAll?: OverridableQuery<
    UseQueryOptions<ICriteriaList, IErrorApiResponse>
  > & {
    args?: { includeInternal?: boolean } & ICriteriaVisibilityFilter;
  };
  getByCode?: OverridableQuery<
    UseQueryOptions<ISuccessApiResponse<ICriteria>>
  > & {
    args?: { code: string };
  };
};

export const useManageCriteria = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();
  const queryClient = useQueryClient();

  const api = useCrud<
    'create' | 'update' | 'remove',
    ICriteria,
    ICriteriaPayload,
    ICriteriaPayload,
    IErrorApiResponse<IValidationError<CommonErrorCodesEnumType>>,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      string,
      ICriteriaValueUsageError
    >,
    IErrorApiResponse<IValidationError>,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      string,
      ICriteriaUsageError
    >
  >(
    {
      baseKey: ConnectQueryKeys.criteria.base(),
      caller: callCoreApi,
      endpoint: `/tenants/api/wtb/tenants/${tenantCode}/criteria`,
    },
    {
      ...options,
      update: {
        ...options.update,
        onSuccess: (...params) => {
          queryClient
            .resetQueries({ queryKey: ConnectQueryKeys.criteria.all() })
            .catch(() => {});
          if (options.update?.onSuccess) {
            options.update.onSuccess(...params);
          }
        },
      },
      create: {
        ...options.create,
        onSuccess: (...params) => {
          queryClient
            .resetQueries({ queryKey: ConnectQueryKeys.criteria.all() })
            .catch(() => {});
          if (options.create?.onSuccess) {
            options.create.onSuccess(...params);
          }
        },
      },
      remove: {
        ...options.remove,
        onSuccess: (...params) => {
          queryClient
            .resetQueries({ queryKey: ConnectQueryKeys.criteria.all() })
            .catch(() => {});
          if (options.remove?.onSuccess) {
            options.remove.onSuccess(...params);
          }
        },
      },
    },
  );

  const listQuery = useQuery({
    queryKey: ConnectQueryKeys.criteria.all(options.getAll?.args),
    queryFn: () =>
      callCoreApi<ICriteriaList>(
        `/tenants/api/wtb/tenants/${tenantCode}/criteria/all?${serialize({
          sortField: 'name',
          sortDirection: 'ASC',
          ...options.getAll?.args,
        })}`,
        {},
      ),
    refetchOnWindowFocus: false,
    enabled: false,
    ...options.getAll,
  });

  const getByCodeQuery = useQuery({
    queryKey: ConnectQueryKeys.criteria.byCode(
      options.getByCode?.args?.code as string,
    ),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<ICriteria>>(
        `/tenants/api/wtb/tenants/${tenantCode}/criteria/by-code/${
          options.getByCode?.args?.code as string
        }`,
        {},
      ),
    refetchOnWindowFocus: false,
    ...omit(options.getByCode, ['args']),
    enabled:
      Boolean(options.getByCode?.args?.code) && options.getByCode?.enabled,
  });

  return restrictApi(options, {
    ...api,
    getAll: listQuery,
    getByCode: getByCodeQuery,
  });
};
